import React, { useEffect, useState } from "react";
import { 
  api, Policy, Audience, getComponents, 
  getPolicies, getAudiences, createPolicy, updatePolicy, deletePolicy 
} from "../api";
import {
  Box, Paper, Stack, Button, Typography, TextField, 
  Checkbox, FormControlLabel, MenuItem, Select,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Alert
} from "@mui/material";
import { Delete, Edit, Add, Remove } from "@mui/icons-material";

type CatalogItem = { kind: string; default_config: any };
type Catalog = { TRIGGER: CatalogItem[]; ACTION: CatalogItem[]; CONSTRAINT: CatalogItem[] };

export default function PoliciesPage() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [catalog, setCatalog] = useState<Catalog>({ TRIGGER: [], ACTION: [], CONSTRAINT: [] });
  const [editing, setEditing] = useState<Policy | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      // Load all data in parallel
      const [policiesData, triggersData, actionsData, constraintsData, audiencesData] = await Promise.all([
        getPolicies(),
        getComponents("TRIGGER"),
        getComponents("ACTION"),
        getComponents("CONSTRAINT"),
        getAudiences(),
      ]);
      
      setPolicies(policiesData);
      setCatalog({ 
        TRIGGER: triggersData, 
        ACTION: actionsData, 
        CONSTRAINT: constraintsData 
      });
      setAudiences(audiencesData);
    } catch (err) {
      console.error("Failed to load data:", err);
      setError("Failed to load data. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { 
    loadData();
  }, []);

  const handleNewPolicy = () => {
    setEditing({ 
      id: null, 
      name: "New Policy", 
      priority: 100, 
      enabled: true, 
      audience_ids: [], 
      components: [] 
    });
    setDialogOpen(true);
  };

  const handleEditPolicy = (policy: Policy) => {
    setEditing({ ...policy });
    setDialogOpen(true);
  };

  const handleSavePolicy = async () => {
    if (!editing) return;

    setLoading(true);
    setError(null);
    try {
      if (editing.id) {
        await updatePolicy(editing.id, editing);
      } else {
        await createPolicy(editing);
      }
      setDialogOpen(false);
      setEditing(null);
      await loadData();
    } catch (err) {
      console.error("Failed to save policy:", err);
      setError("Failed to save policy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePolicy = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this policy?")) return;

    setLoading(true);
    setError(null);
    try {
      await deletePolicy(id);
      await loadData();
    } catch (err) {
      console.error("Failed to delete policy:", err);
      setError("Failed to delete policy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const addComponent = (type: "TRIGGER" | "ACTION" | "CONSTRAINT") => {
    if (!editing) return;
    
    const catalogItems = catalog[type];
    if (!catalogItems || catalogItems.length === 0) {
      setError(`No ${type} components available`);
      return;
    }
    
    const firstItem = catalogItems[0];
    const newComponent = {
      type,
      kind: firstItem.kind,
      config: firstItem.default_config || {}
    };
    
    setEditing({
      ...editing,
      components: [...(editing.components || []), newComponent]
    });
  };

  const removeComponent = (index: number) => {
    if (!editing) return;
    
    const components = [...(editing.components || [])];
    components.splice(index, 1);
    setEditing({ ...editing, components });
  };

  const updateComponent = (index: number, updates: any) => {
    if (!editing) return;
    
    const components = [...(editing.components || [])];
    components[index] = { ...components[index], ...updates };
    setEditing({ ...editing, components });
  };

  const updateComponentConfig = (index: number, config: any) => {
    if (!editing) return;
    
    const components = [...(editing.components || [])];
    components[index] = { ...components[index], config };
    setEditing({ ...editing, components });
  };

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          <Button 
            variant="contained" 
            onClick={handleNewPolicy}
            disabled={loading}
            startIcon={<Add />}
          >
            New Policy
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => loadData()} 
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Policies</Typography>
        <Stack gap={2}>
          {policies.map(policy => (
            <Paper key={String(policy.id)} sx={{ p: 2 }} elevation={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="start">
                <Box>
                  <Typography variant="h6">{policy.name}</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Priority: {policy.priority} • Status: {policy.enabled ? "Enabled" : "Disabled"}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {policy.audience_ids?.map(id => (
                      <Chip key={id} label={id} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {policy.components?.length || 0} components
                  </Typography>
                </Box>
                <Stack direction="row" gap={1}>
                  <IconButton onClick={() => handleEditPolicy(policy)} color="primary">
                    <Edit />
                  </IconButton>
                  <IconButton 
                    onClick={() => policy.id && handleDeletePolicy(policy.id)} 
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
          {policies.length === 0 && !loading && (
            <Typography color="text.secondary">No policies configured.</Typography>
          )}
        </Stack>
      </Paper>

      {/* Policy Editor Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editing?.id ? "Edit Policy" : "Create Policy"}
        </DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Policy Name"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                fullWidth
              />
              
              <TextField
                label="Priority"
                type="number"
                value={editing.priority}
                onChange={(e) => setEditing({ ...editing, priority: parseInt(e.target.value) || 0 })}
                helperText="Higher numbers = higher priority"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editing.enabled}
                    onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
                  />
                }
                label="Enabled"
              />
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>Audiences</Typography>
                <Select
                  multiple
                  value={editing.audience_ids}
                  onChange={(e) => setEditing({ 
                    ...editing, 
                    audience_ids: typeof e.target.value === 'string' 
                      ? e.target.value.split(',') 
                      : e.target.value 
                  })}
                  fullWidth
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {audiences.map((aud) => (
                    <MenuItem key={aud.id} value={aud.id}>
                      {aud.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Components</Typography>
                <Stack direction="row" gap={1} sx={{ mb: 2 }}>
                  <Button 
                    size="small" 
                    onClick={() => addComponent("TRIGGER")}
                    startIcon={<Add />}
                    variant="outlined"
                  >
                    Add Trigger
                  </Button>
                  <Button 
                    size="small" 
                    onClick={() => addComponent("ACTION")}
                    startIcon={<Add />}
                    variant="outlined"
                  >
                    Add Action
                  </Button>
                  <Button 
                    size="small" 
                    onClick={() => addComponent("CONSTRAINT")}
                    startIcon={<Add />}
                    variant="outlined"
                  >
                    Add Constraint
                  </Button>
                </Stack>

                <Stack gap={2}>
                  {(editing.components || []).map((comp, idx) => (
                    <Paper key={idx} sx={{ p: 2 }} variant="outlined">
                      <Stack direction="row" justifyContent="space-between" alignItems="start">
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" gap={2} alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">
                              {comp.type}
                            </Typography>
                            <Select
                              size="small"
                              value={comp.kind}
                              onChange={(e) => {
                                const newKind = e.target.value;
                                const catalogItem = catalog[comp.type as keyof Catalog]?.find(
                                  item => item.kind === newKind
                                );
                                updateComponent(idx, { 
                                  kind: newKind,
                                  config: catalogItem?.default_config || {}
                                });
                              }}
                            >
                              {catalog[comp.type as keyof Catalog]?.map((item) => (
                                <MenuItem key={item.kind} value={item.kind}>
                                  {item.kind}
                                </MenuItem>
                              ))}
                            </Select>
                          </Stack>
                          
                          <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            label="Configuration (JSON)"
                            value={JSON.stringify(comp.config || {}, null, 2)}
                            onChange={(e) => {
                              try {
                                const config = JSON.parse(e.target.value);
                                updateComponentConfig(idx, config);
                              } catch {
                                // Invalid JSON, don't update
                              }
                            }}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                        
                        <IconButton 
                          onClick={() => removeComponent(idx)}
                          color="error"
                          size="small"
                        >
                          <Remove />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}
                  {!(editing.components || []).length && (
                    <Typography color="text.secondary" variant="body2">
                      No components configured. Add triggers, actions, and constraints above.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePolicy} variant="contained" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
